import { Check, Copy, Globe, LockKey, Plus, Users } from '@phosphor-icons/react';
import { useState } from 'react';
import { api } from '../lib/api';
import { uploadDialogueMedia } from '../lib/dialogueMedia';
import type { ProjectSnapshot } from '../types';
import { Modal } from './Modal';

interface ShareModalProps {
  open: boolean;
  project: ProjectSnapshot;
  authenticated: boolean;
  onClose: () => void;
  onRequireLogin: () => void;
  onProjectSaved: (project: ProjectSnapshot) => void;
}

export function ShareModal({ open, project, authenticated, onClose, onRequireLogin, onProjectSaved }: ShareModalProps) {
  const [visibility, setVisibility] = useState<'public' | 'restricted'>('public');
  const [email, setEmail] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const addEmail = () => {
    const clean = email.trim().toLowerCase();
    if (!clean || emails.includes(clean)) return;
    setEmails([...emails, clean]);
    setEmail('');
  };

  const createLink = async () => {
    if (!authenticated) { onClose(); onRequireLogin(); return; }
    setLoading(true);
    setError('');
    try {
      let projectId = project.id;
      if (!projectId) {
        const prepared = await uploadDialogueMedia(project);
        const result = await api.saveProject(prepared);
        projectId = result.project.id;
        onProjectSaved(result.project);
      }
      const result = await api.createShare(projectId, { visibility, emails });
      setUrl(result.url);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '공유 링크를 만들지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Modal open={open} onClose={onClose} title="배치안 공유" description="받는 사람은 3D 공간을 둘러볼 수 있으며 편집할 수 없습니다." className="share-modal">
      <div className="share-options">
        <button className={visibility === 'public' ? 'active' : ''} type="button" onClick={() => setVisibility('public')}>
          <Globe size={22} /><span><strong>링크가 있는 모든 사람</strong><small>로그인 없이 열람</small></span>{visibility === 'public' && <Check size={17} />}
        </button>
        <button className={visibility === 'restricted' ? 'active' : ''} type="button" onClick={() => setVisibility('restricted')}>
          <LockKey size={22} /><span><strong>지정한 사용자만</strong><small>이메일 확인 후 열람</small></span>{visibility === 'restricted' && <Check size={17} />}
        </button>
      </div>
      {visibility === 'restricted' && (
        <div className="invite-field">
          <label htmlFor="invite-email">초대할 이메일</label>
          <div><input id="invite-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addEmail(); } }} placeholder="name@company.com" /><button type="button" onClick={addEmail}><Plus size={17} /> 추가</button></div>
          {!!emails.length && <div className="email-list">{emails.map((item) => <button key={item} type="button" onClick={() => setEmails(emails.filter((value) => value !== item))}>{item}<span>×</span></button>)}</div>}
        </div>
      )}
      {url ? (
        <div className="generated-link"><Globe size={18} /><span>{url}</span><button type="button" onClick={copy}>{copied ? <Check size={17} /> : <Copy size={17} />}{copied ? '복사됨' : '복사'}</button></div>
      ) : (
        <button className="button button-primary button-block" type="button" disabled={loading} onClick={createLink}><Users size={18} />{loading ? '링크 만드는 중...' : authenticated ? '공유 링크 만들기' : '로그인하고 공유'}</button>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
    </Modal>
  );
}
